/**
 * Drag and drop in the planner's week and day views (#2038): a shift block moves to
 * another day (By Group and By Employee) or to another employee row (By Employee). The
 * grid only reports the drop; the page asks the series scope and the published-shift
 * confirmation and saves through PUT /shifts/{id}, so the overlap and absence checks run
 * as for any edit and a refused move leaves the block where it was.
 *
 * Pointer only, with a 6 px activation distance so a click still opens the shift.
 * Keyboard users keep the edit dialog.
 */
import { createContext, forwardRef, useContext, useState, type HTMLAttributes, type ReactNode } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import type { Shift } from '@/types/shifts';
import type { ViewMode } from '@/utils/shiftHelpers';

/** Where a dragged shift was dropped: the row and the day of the cell. */
export interface ShiftDropTarget {
  resourceId: string;
  dateKey: string; // YYYY-MM-DD
}

/** What a drop asks to change. */
export interface ShiftMove {
  employeeId: string;
  shiftDate: string; // YYYY-MM-DD
}

/**
 * The move a drop asks for, or null when it changes nothing or is not allowed. By Group:
 * the day only (a group row is not an employee). By Employee: the day and the employee
 * of the row; the "Unassigned" row is no target.
 */
export function planShiftDrop(shift: Shift, target: ShiftDropTarget, viewMode: ViewMode): ShiftMove | null {
  const currentEmployee = shift.employee_id ?? '';
  const currentDate = shift.shift_date ?? '';
  let employeeId = currentEmployee;
  if (viewMode === 'byEmployee') {
    // The open row (#2040) takes only open shifts, which stay open; unassigning is an edit.
    if (target.resourceId === 'unassigned') {
      if (currentEmployee) return null;
    } else {
      employeeId = target.resourceId;
    }
  }
  if (employeeId === currentEmployee && target.dateKey === currentDate) return null;
  return { employeeId, shiftDate: target.dateKey };
}

const DndEnabledContext = createContext(false);
// Which blocks may be dragged (#2043): a group lead drags only the shifts of their groups.
const CanDragContext = createContext<(shift: Shift) => boolean>(() => true);

/** Wraps the grid; renders the dragged shift as an overlay label. */
export function ShiftDndProvider({
  enabled,
  onDrop,
  renderOverlay,
  canDrag,
  children,
}: {
  enabled: boolean;
  onDrop: (shift: Shift, target: ShiftDropTarget) => void;
  renderOverlay: (shift: Shift) => ReactNode;
  /** Blocks for which this returns false stay put (#2043). Absent: every block drags. */
  canDrag?: (shift: Shift) => boolean;
  children: ReactNode;
}) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const [active, setActive] = useState<Shift | null>(null);
  if (!enabled) return <>{children}</>;

  const handleStart = (event: DragStartEvent) => {
    setActive((event.active.data.current?.shift as Shift | undefined) ?? null);
  };
  const handleEnd = (event: DragEndEvent) => {
    setActive(null);
    const shift = event.active.data.current?.shift as Shift | undefined;
    const target = event.over?.data.current as ShiftDropTarget | undefined;
    if (shift && target) onDrop(shift, target);
  };

  return (
    <DndEnabledContext.Provider value={true}>
      <CanDragContext.Provider value={canDrag ?? (() => true)}>
      <DndContext sensors={sensors} onDragStart={handleStart} onDragEnd={handleEnd} onDragCancel={() => setActive(null)}>
        {children}
        <DragOverlay dropAnimation={null}>{active ? renderOverlay(active) : null}</DragOverlay>
      </DndContext>
      </CanDragContext.Provider>
    </DndEnabledContext.Provider>
  );
}

/** Drag wiring of a shift block; inert outside an enabled ShiftDndProvider. */
export function useShiftDraggable(shift: Shift) {
  const canDrag = useContext(CanDragContext);
  const enabled = useContext(DndEnabledContext) && canDrag(shift);
  const { listeners, setNodeRef, isDragging } = useDraggable({
    id: `shift-${shift.id}`,
    data: { shift },
    disabled: !enabled,
  });
  return { enabled, listeners: enabled ? listeners : undefined, setNodeRef, isDragging };
}

/** A grid cell that takes a dropped shift; a plain div outside an enabled provider. */
export const ShiftDropCell = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & { resourceId: string; date: Date }
>(function ShiftDropCell({ resourceId, date, className, ...props }, ref) {
  const enabled = useContext(DndEnabledContext);
  const dateKey = format(date, 'yyyy-MM-dd');
  const { setNodeRef, isOver } = useDroppable({
    id: `cell|${resourceId}|${dateKey}`,
    data: { resourceId, dateKey } satisfies ShiftDropTarget,
    disabled: !enabled,
  });
  return (
    <div
      ref={(node) => {
        setNodeRef(node);
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      className={cn(className, enabled && isOver && 'ring-2 ring-primary ring-offset-1')}
      data-drop-target={enabled ? `${resourceId}|${dateKey}` : undefined}
      {...props}
    />
  );
});
